import { createTheme, darken, lighten, PaletteColor, PaletteColorOptions } from '@mui/material/styles'
import '@mui/x-date-pickers/themeAugmentation'

// Import manrope font weights from @fontsource
import '@fontsource/manrope/300.css' // Light weight
import '@fontsource/manrope/400.css' // Regular weight
import '@fontsource/manrope/500.css' // Medium weight
import '@fontsource/manrope/600.css' // Medium weight
import '@fontsource/manrope/700.css' // Bold weight

declare module '@mui/material/styles' {
  interface TypeText {
    greyOut: string
    darkBackground: string
    contrastText: string
  }
  interface Palette {
    custom: {
      linkLight: PaletteColor
      linkDark: PaletteColor
    }
  }
  interface TypeBackground {
    default: string
    light: string
  }
  interface PaletteOptions {
    custom?: {
      linkLight: PaletteColorOptions & { main: string; light: string; dark: string; contrastText: string }
      linkDark: PaletteColorOptions & { main: string; light: string; dark: string; contrastText: string }
    }
  }
}

/* ------------------------ Colors ------------------------ */
const primary = '#1a4654'
const secondary = '#eda11a'
const secondaryLight = '#e7ae4c'
const success = '#4cd964'
const error = '#e71717'
const text = '#012b33'
const contrastText = '#ffffff'
const background = '#1A4653'
const linkLight = '#FFFFFF'
const linkDark = '#000000'
const textDarkBackground = '#303030'
const greyOut = '#A0A0A0'
const lightBackground = '#F3F3F3'
const inputBackground = '#e5e5e5'

/* ---------------------- Border Radius ---------------------- */
export const INPUT_BORDER_RADIUS = '10px'
const DATE_PICKER_BORDER_READIUS = '10px'
export const DIALOG_BORDER_RADIUS = '20px'

/* ---------------------- Typography ---------------------- */

export const LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER = 0.9
export const TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER = 0.9
export const MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER = 1
export const ICON_FONT_SIZE_SMALL = '25px'
export const ICON_FONT_SIZE_MEDIUM = '30px'
export const ICON_FONT_SIZE_LARGE = '40px'

export const theme = createTheme({
  /*--------------------- Palette ---------------------*/
  palette: {
    primary: {
      main: primary,
      light: lighten(primary, 0.2),
      dark: darken(primary, 0.2),
      contrastText,
    },
    secondary: {
      main: secondary,
      light: secondaryLight,
      dark: darken(secondary, 0.2),
      contrastText,
    },
    success: {
      main: success,
      contrastText,
    },
    error: {
      main: error,
      contrastText,
    },
    background: {
      default: background,
      light: lightBackground,
    },
    text: {
      primary: text,
      secondary: text,
      greyOut,
      darkBackground: textDarkBackground,
      contrastText,
    },
    custom: {
      linkLight: {
        main: linkLight,
        light: lighten(primary, 0.2),
        dark: darken(primary, 0.2),
        contrastText,
      },
      linkDark: {
        main: linkDark,
        light: lighten(linkDark, 0.1),
        dark: darken(linkDark, 0.1),
        contrastText,
      },
    },
  },
  /*--------------------- Breakpoints ---------------------*/
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 600,
      laptop: 960,
      desktop: 1280,
    },
  },
  /*--------------------- Typography ---------------------*/
  typography: {
    fontSize: 21,
    fontFamily: '"Manrope", Arial, sans-serif',
    h1: { fontSize: '20px', fontWeight: 600 },
    h2: { fontSize: '18px', fontWeight: 600 },
    h3: { fontSize: '16px', fontWeight: 600 },
    h4: { fontSize: '14px', fontWeight: 600 },
    h5: { fontSize: '12px', fontWeight: 400 },
    body1: { fontSize: '16px', fontWeight: 400 },
    body2: { fontSize: '14px', fontWeight: 400 },
    caption: { fontSize: '12px', fontWeight: 400 },
    overline: { fontSize: '10px', fontWeight: 400 },
    button: { fontSize: '16px', fontWeight: 400, textTransform: 'none' },
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  /*--------------------- Components Overrides ---------------------*/
  components: {
    // --------------------- CssBaseline ---------------------
    MuiCssBaseline: {
      styleOverrides: {
        // Prevent the body from scrolling — all scrolling is handled by the content area in GlobalPageWrapper
        html: { overflow: 'hidden', height: '100%' },
        body: { overflow: 'hidden', height: '100%' },
      },
    },
    // --------------------- Typography ---------------------
    MuiTypography: {
      styleOverrides: {
        h1: ({ theme }) => ({
          fontSize: theme.typography.h1.fontSize,
          fontWeight: theme.typography.h1.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.h1.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.h1.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        h2: ({ theme }) => ({
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h2.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.h2.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.h2.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        h3: ({ theme }) => ({
          fontSize: theme.typography.h3.fontSize,
          fontWeight: theme.typography.h3.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h3.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.h3.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.h3.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        h4: ({ theme }) => ({
          fontSize: theme.typography.h4.fontSize,
          fontWeight: theme.typography.h4.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h4.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.h4.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.h4.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        h5: ({ theme }) => ({
          fontSize: theme.typography.h5.fontSize,
          fontWeight: theme.typography.h5.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h5.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.h5.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.h5.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        body1: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.body1.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.body1.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        body2: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body2.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.body2.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.body2.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        caption: ({ theme }) => ({
          fontSize: theme.typography.caption.fontSize,
          fontWeight: theme.typography.caption.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.caption.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.caption.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.caption.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        overline: ({ theme }) => ({
          fontSize: theme.typography.overline.fontSize,
          fontWeight: theme.typography.overline.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.overline.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.overline.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.overline.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
        button: ({ theme }) => ({
          fontSize: theme.typography.button.fontSize,
          fontWeight: theme.typography.button.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.button.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          // Tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.button.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          // Laptop and Desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.button.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
      },
    },
    // --------------------- Button ---------------------
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: INPUT_BORDER_RADIUS,
          fontSize: theme.typography.button.fontSize,
          fontWeight: theme.typography.button.fontWeight,
          padding: '8px 30px',
          borderWidth: 2,
          //mobile
          [theme.breakpoints.down('tablet')]: {
            fontSize: `calc(${theme.typography.button.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          //tablet
          [theme.breakpoints.between('tablet', 'laptop')]: {
            fontSize: `calc(${theme.typography.button.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
          //laptop and desktop
          [theme.breakpoints.between('laptop', 'desktop')]: {
            fontSize: `calc(${theme.typography.button.fontSize} * ${LAPTOP_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
          },
        }),
      },
    },
    // --------------------- InputLabel ---------------------
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          lineHeight: '24px',
          left: 15,
          // Mobile
          [theme.breakpoints.down('tablet')]: {
            fontSize: `calc(${theme.typography.body1.fontSize} * ${TABLET_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
            lineHeight: '31px',
          },
        }),
        shrink: {
          left: 1,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSelect-select': {
            // Mobile
            [theme.breakpoints.down('tablet')]: {
              padding: '10px 30px',
            },
          },
        }),
      },
    },
    // --------------------- OutlinedInput ---------------------
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: INPUT_BORDER_RADIUS,
          backgroundColor: theme.palette.background.light,
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          lineHeight: '24px',
          // Mobile
          [theme.breakpoints.down('tablet')]: {
            fontSize: theme.typography.body1.fontSize,
            lineHeight: '31px',
          },
          '& svg': {
            zIndex: 1,
          },
        }),
        input: {
          padding: '15px 30px',
          position: 'relative',
          zIndex: 1,
        },
        notchedOutline: {
          backgroundColor: inputBackground,
          //borderColor: inputBackground,
        },
      },
    },
    // --------------------- Chip ---------------------
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '0 20px',
          borderRadius: '50px',
          height: 'auto',
          minHeight: '40px',
          // Mobile - allow text wrapping
          [theme.breakpoints.down('tablet')]: {
            height: 'auto',
            minHeight: '36px',
            padding: '12px 16px',
            borderRadius: '20px',
          },
        }),
        label: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.fontWeightMedium,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          textAlign: 'center',
          lineHeight: 1.2,
          // Mobile
          [theme.breakpoints.down('tablet')]: {
            fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})`,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            textAlign: 'center',
            lineHeight: 1.3,
          },
        }),
      },
    },
    // --------------------- TableCell ---------------------
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 400,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          p: {
            fontSize: theme.typography.body1.fontSize,
            fontWeight: 400,
            // Mobile
            [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          },
        }),
      },
    },
    // --------------------- FormControlLabel ---------------------
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 400,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          lineHeight: '24px',
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.caption.fontSize,
          fontWeight: theme.typography.caption.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    // --------------------- Tooltip ---------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
        }),
        popper: {
          position: 'fixed',
        },
      },
    },
    // --------------------- Alert ---------------------
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    // --------------------- List ---------------------
    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.body1.fontWeight,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    // --------------------- ListItemText ---------------------
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    // --------------------- Datepicker ---------------------
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          position: 'relative',
          zIndex: 1,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 350,
          borderRadius: DATE_PICKER_BORDER_READIUS,
          backgroundColor: theme.palette.primary.contrastText,
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekContainer: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          '&.Mui-selected': {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          },
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          },
        }),
      },
    },
    // --------------------- Dialog ---------------------
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: DIALOG_BORDER_RADIUS,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '20px 43px',
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          textTransform: 'uppercase',
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.h2.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
          [theme.breakpoints.down('tablet')]: { fontSize: 17 },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '20px 43px',
          fontSize: theme.typography.body1.fontSize,
          // Mobile
          [theme.breakpoints.down('tablet')]: { fontSize: `calc(${theme.typography.body1.fontSize} * ${MOBILE_TYPOGRAPHY_FONT_SIZE_MULTIPLIER})` },
        }),
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '20px 43px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
        }),
      },
    },
  },
})

/*--------------------- Breakpoints ---------------------*/
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false // removes the `xs` breakpoint
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true // adds the `mobile` breakpoint
    tablet: true
    laptop: true
    desktop: true
  }
}
/* ------------------------ Variables ------------------------ */
export const PAGE_TOP_PADDING = { desktop: 2, tablet: 2, mobile: 1 }
export const PAGE_BOTTOM_PADDING = { desktop: '120px', tablet: '120px', mobile: '120px' }
export const PAGE_LEFT_RIGHT_PADDING = { desktop: '5vw', tablet: '5vw', mobile: '5vw' }
export const FOOTER_HEIGHT = { desktop: '240px', tablet: '240px', mobile: '430px' }
export const PAGE_MAX_WIDTH = { desktop: '1480px', tablet: '100%', mobile: '100%' }
export const PAGE_WIDTH = { desktop: '90%', tablet: '90%', mobile: '90%' }
export const HEADER_HEIGHT = { desktop: '80px', tablet: '80px', mobile: '70px' }
export const HEADER_BANNER_HEIGHT = { desktop: '350px', tablet: '250px', mobile: '150px' }
export const BEACH_PLAN_HEIGHT = { desktop: '80vh', tablet: '80vh', mobile: '50vh' }
export default theme
